import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TokenService } from './token.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, of, retry, throwError } from 'rxjs';
import { jwtDecode } from "jwt-decode";
import { IndexedDBService } from './indexed-db.service';
import { UserIndexedDB } from '../interfaces/user-indexed-db';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { environment } from '@env/environment';
import { AppleUserProfile, AuthTokenResponse, OriginDomainData, ProviderId, ProviderName } from '../models';
import { AuthErrorsService } from '@app/core/services/auth-errors.service';
import { CountryService } from './country.service';
import { QueryParamHandlerService } from './query-param-handler.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private authErrors = inject(AuthErrorsService);
  private translate = inject(TranslateService);
  private indexedDBService = inject(IndexedDBService);
  private qParamsHandlerService = inject(QueryParamHandlerService);
  private tokenService = inject(TokenService);
  public accounts: WritableSignal<UserIndexedDB[] | undefined> = signal(undefined);
  public continue_as_account: UserIndexedDB | undefined;
  private countryService = inject(CountryService);
  public userCountry = (computed(() => this.countryService.userCountry()));
  public isLoadingAFederated = signal<boolean | undefined>(undefined);
  public isReturningFromRedirect = signal<boolean>(false);
  public originDomainData: OriginDomainData | undefined;

  accountVisibility = {
    continue_as: false,
    accounts_list: false
  };

  fingerPrint: string | undefined;

  public socialProviders: { name: ProviderName; className: string; iconClass: string; imageSrc: string }[] = [
    {
      name: 'Facebook',
      className: 'facebook-sign-in-btn',
      iconClass: 'icon-facebook',
      imageSrc: '',
    },
    {
      name: 'Google',
      className: 'google-sign-in-btn',
      iconClass: '',
      imageSrc: "assets/images/google.svg",
    },
    {
      name: 'Apple ID',
      className: 'apple-sign-in-btn',
      iconClass: '',
      imageSrc: "assets/images/apple-logo.svg",
    },
    {
      name: 'LinkedIn',
      className: 'linkedin-sign-in-btn',
      iconClass: 'icon-linkedin',
      imageSrc: "",
    }
  ];

  constructor(
  ) {
    this.countryService.loadUserCountry();

    effect(() => {
      // Escucha cuando se mande la orden de iniciar automáticamente una sesión
      const hasToLoginAnAccount = this.qParamsHandlerService.hasToLoginAuto();
      if (hasToLoginAnAccount) {
        const { fuente, username, password, token } = hasToLoginAnAccount;
        this.login({ username, password, token }, fuente as ProviderId).subscribe();
      }

      // Escucha cuando se mande la orden de cargar la cuenta para continuar como...
      const hasToContinueAs = this.qParamsHandlerService.loadContinueAs();
      if (hasToContinueAs) {
        this.accountVisibility = {
          continue_as: true,
          accounts_list: false,
        }
        this.continue_as_account = hasToContinueAs;

      }

    });
  }

  isAuthenticated(): boolean {
    return this.tokenService.isTokenValid();
  }


  // Elimina espacios al inicio/final y reemplaza múltiples espacios internos por uno solo
  cleanString(texto: string): string {
    return texto.trim().replace(/\s+/g, ' ');
  }

  formatAccountData(credentials: { fullname: string, password: string, email: string, response: string }) {
    const token = window.btoa(credentials.password);
    let accountData: { name: string, username: string, token: string, business?: boolean } = {
      name: this.cleanString(credentials.fullname),
      username: credentials.email,
      token
    }

    return accountData;
  }


  //CREACIÓN DE CUENTA
  /**
   * Creates a new user account by sending the provided credentials to the server.
   *
   * @param credentials - An object containing the user's account details:
   *   - `fullname`: The full name of the user.
   *   - `password`: The password for the new account.
   *   - `email`: The email address of the user.
   *   - `response`: The reCAPTCHA token for verification.
   * @returns An `Observable` that emits the server's response upon success or an error object upon failure.
   *
   * @remarks
   * - The method formats the account data before sending it to the server.
   * - A custom error object is created and logged to the console if the request fails.
  * - The error message is retrieved from the i18n error catalog based on the error code.
   *
   * @throws Will throw an error observable containing a custom error object if the HTTP request fails.
   */
  createAccount(credentials: { fullname: string, password: string, email: string, response: string }): Observable<any> {
    const body = this.formatAccountData(credentials);
    const headers = new HttpHeaders({ 'X-Recaptcha-Token': credentials.response });
    return this.http.post(`${environment.keyApi}/auth/register`, body, { headers }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(errorResponse => {
        const { error, status } = errorResponse?.error;
        const customError = {
          ...error,
          msg: this.authErrors.getMessage(error?.code) || this.authErrors.getMessage('default'),
          status: status,
        };
        return throwError(() => customError);
      })
    )
  }


  sendVerificationToConfirm(credentials: { email: string, response: string | '' }): Observable<any> {
    //Se envía código de verificación al email ingresado para confirmar la cuenta
    const body = { email: credentials.email }
    const headers = new HttpHeaders({ 'X-Recaptcha-Token': credentials.response });
    return this.http.post(`${environment.keyApi}/auth/sendUserConfirmAccount`, body, { headers }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(errorResponse => {
        const { error, status } = errorResponse?.error;
        const customError = {
          ...error,
          msg: this.authErrors.getMessage(error?.code) || this.authErrors.getMessage('default'),
          status: status,
        };
        return throwError(() => customError);
      })
    )
  }

  confirmAccount(credentials: { email: string, code: number }, captchaResponse: string): Observable<any> {
    // Confirmación de cuenta con el código de verificación
    const body = credentials;
    const headers = new HttpHeaders({ 'X-Recaptcha-Token': captchaResponse });
    return this.http.post(`${environment.keyApi}/auth/confirmAccount`, body, { headers }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(errorResponse => {
        const { error, status } = errorResponse?.error;
        const customError = {
          ...error,
          msg: this.authErrors.getMessage(error?.code) || this.authErrors.getMessage('default'),
          status: status,
        };
        return throwError(() => customError);
      })
    )
  }
  //CREACIÓN DE CUENTA END

  /**
   * Verifica si existe una cuenta asociada a un correo o celular.
   * @param identifier Puede ser un correo o un número de celular.
   * @returns Observable<boolean> - `true` si la cuenta existe, `false` si no.
   */
  checkAccountExists(identifier: AbstractControl): Observable<any> {

    const type: 'email' | 'phone' | any = this.getIdentifierType(identifier.value);
    // const inputValue: string = type == 'phone' ? encodeURIComponent(identifier.value) : identifier.value; //Valor del input
    const inputValue = this.getPhoneFormatted(identifier.value);
    const params = new HttpParams().set(type, inputValue);
    const identifierType = type == 'phone'
      ? this.translate.instant('auth.common.userInputType.phone')
      : this.translate.instant('auth.common.userInputType.email');

    return this.http.get(`${environment.keyApi}/auth/verify`, { params }).pipe(
      map((response: any) => {
        response.type = identifierType;
        return response
      }), // Si la API no devuelve 'exists', retorna `false`
      catchError(errorResponse => {
        const { error, status } = errorResponse?.error;
        const customError = {
          ...error,
          msg: this.authErrors.getMessage(error?.code, { userInputType: identifierType }) || this.authErrors.getMessage('default'),
          status: status,
        };
        return throwError(() => customError);
      })
    );
  }


  /**
   * Generates the parameters required for signing in based on the provided credentials and provider.
   *
   * @returns A string representing the URL-encoded parameters for the sign-in request.
   */
  getSignInParams(credentials: { username?: string, password?: string, token?: string }, provider?: string, apple_user_profile?: AppleUserProfile) {
    // const originalUsername = credentials.username;
    const username = this.getPhoneFormatted(credentials.username || '', true);

    if (provider == 'form') {
      //Params para autenticación por formulario
      return new HttpParams()
        .set('client_id', 'users')
        .set('grant_type', 'password')
        .set('username', username || '')
        .set('password', credentials.password || '')
      // .set('X-Request-Context', this.fingerPrint || ''); // No es requerido
    }

    //Si el inicio de sesión es por federado (provider facebook-test y google)
    let subject_token_type = 'urn:ietf:params:oauth:token-type:access_token'
    if (provider == 'google-id' || provider == 'apple') {
      //Params cuando es autenticación por google o apple, el federado devuelve id_token
      subject_token_type = 'urn:ietf:params:oauth:token-type:id_token';
    }

    const body = new URLSearchParams();
    body.append('subject_issuer', provider || '');
    body.append('subject_token', credentials.token || '');
    body.append('grant_type', 'urn:ietf:params:oauth:grant-type:token-exchange');
    body.append('client_id', 'broker-users');
    // body.append('client_secret', 'fSRl7P9ygphe4SHwQgvK3Qjih55Bq4wT');
    body.append('subject_token_type', subject_token_type);
    // .set('X-Request-Context', this.fingerPrint || ''); // No es requerido

    if (provider == 'apple' && apple_user_profile) {
      //Si la cuenta es de Apple, se envía la data del user que devuelve apple
      body.append('user_profile', JSON.stringify(apple_user_profile));
      // Data que contiene apple_user_profile =
      // apple_user_profile=  {
      //     "name": {
      //         "firstName": "Steven",
      //         "lastName": "Sanchez"
      //     },
      //     "email": "sanchezsteven.ss@gmail.com"
      // }
      // Esta condición es especifica para cuando el usuario acaba de crear cuenta con Apple y se inicia sesión,
      // Apple solo retorna el email y name del usuario solo la primera vez que acepta ingresar a nuestra app.
    }

    return body.toString();
  }

  /**
   *
   * @param credentials
   * Cuando sea por federado, credentials solo trae el token (accessToken o idToken) para armar los params de inicio de sesión
   * @param provider
   * @param apple_user_profile
   * @returns
   */
  login(credentials: { username?: string, password?: string, token?: string }, provider?: ProviderId, apple_user_profile?: AppleUserProfile): Observable<any> {

    const url = `${environment.keyApi}/protocol/openid-connect/token`;
    const body = this.getSignInParams(credentials, provider, apple_user_profile);

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    // const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded', 'X-Request-Context': this.fingerPrint });
    return this.http.post(url, body, { headers }).pipe(
      map((response: any) => {
        // Guarda el token en el para agregarlo a los headers
        if (!('error' in response)) {
          this.handleAccessResponse({ ...response, password: credentials.password });
        }
        return response;
      }),
      catchError(errorResponse => {

        const { error, status } = errorResponse?.error;
        let errorObj = error;
        if (error === 'invalid_grant') {
          errorObj = { code: 'invalid_grant' };
        }
        const identifierType = this.getIdentifierType(credentials.username || '');
        const typeText = identifierType == 'phone'
          ? this.translate.instant('auth.common.userInputType.phone')
          : this.translate.instant('auth.common.userInputType.email');
        const msgError = errorObj?.code == "103"
          ? this.authErrors.getMessage(errorObj?.code, { userInputType: typeText }) || this.authErrors.getMessage('default')
          : this.authErrors.getMessage(errorObj?.code);

        const customError = {
          ...errorObj,
          msg: msgError || this.authErrors.getMessage('default'),
          status: status,
        };
        return throwError(() => customError);
      })

    )
  }


  handleAccessResponse(JWTToken: AuthTokenResponse) {
    const { access_token, password } = JWTToken;
    this.setUserData({ access_token, password });
    this.saveTokenCookies(JWTToken); // Espera que termine de guardar cookies

    this.loginPHP().subscribe((response) => {
      // Se recibe la data de inicio de keycloak en PHP
      const canContinue = this.qParamsHandlerService.handleAccessToRedirect();
      if (canContinue) {
        this.redirectToApp();// Se ejecuta solo después de guardar las cookies
      }
    });
  }

  setCookie(cookieValue: string) {
    // El objeto de opciones de la cookie es donde se debe hacer el cambio
    const expire = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000); // 1 año
    this.tokenService.saveToken('edutin_session', cookieValue, expire);
  }


  getRequestOptions(): any {
    const token = this.tokenService.getToken();
    if (!token) return null;

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + token
    });

    if (!headers) throw new Error(this.translate.instant('auth.common.noTokenAvailable'));

    return { headers, withCredentials: true };
  }

  loginPHP(): Observable<any> {
    const requestOptions = this.getRequestOptions();
    if (!requestOptions) throw new Error(this.translate.instant('auth.common.noTokenAvailable'));
    return this.http.get<any>(`${environment.domain}/auths/login.json`, requestOptions).pipe(
      retry(1),
      catchError(error => {
        console.error('Error en Back login:', error);
        return of({ success: false, error: error });
      })
    );
  }

  logoutPHP(): Observable<any> {
    const requestOptions = this.getRequestOptions();
    if (!requestOptions) {
      return of(null);
      throw new Error(this.translate.instant('auth.common.noTokenAvailable'));
    };
    return this.http.get(`${environment.domain}/auths/logout`, requestOptions).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(error => {
        return of(error)
      })
    );
  }


  backendLogout(): Observable<any> {
    if (!this.tokenService.getDecodedToken()) return of(null);

    const provider = this.tokenService.getDecodedToken()?.fuente
    const isFormProvider = provider && (provider == 'form' || provider == '');

    const url = `${environment.keyApi}/protocol/openid-connect/logout`;
    const body = new HttpParams()
      .set('client_id', isFormProvider ? 'users' : 'broker-users')
      .set('refresh_token', this.tokenService.getCookie('refreshToken') ?? '')

    const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });
    return this.http.post(url, body, { headers }).pipe(
      map((response: any) => {
        return response;
      }),
      catchError(errorResponse => {
        return throwError(() => errorResponse);
      })

    )
  }


  saveTokenCookies(JWTToken: AuthTokenResponse) {
    // Almacena los token obtenidos en las cookies
    const { access_token, expires_in, refresh_expires_in, refresh_token } = JWTToken;
    const now = new Date();
    const accessTokenExp = new Date(now.getTime() + expires_in * 1000);
    const refreshTokenExp = new Date(now.getTime() + refresh_expires_in * 1000);
    this.tokenService.saveToken('accessToken', access_token, accessTokenExp);
    this.tokenService.saveToken('refreshToken', refresh_token, refreshTokenExp);
  }


  authenticatedUser = signal<any>(null); // Guardará la respuesta del usuario
  setUserData(accessToken: { access_token: string, password: any }) {
    const { access_token, password } = accessToken;
    const decodedToken: any = jwtDecode(access_token);
    if (decodedToken?.id) {
      this.authenticatedUser.set(decodedToken);
      const formattedUser = this.indexedDBService.formatUserData({ ...decodedToken, password });
      this.indexedDBService.addUser(formattedUser).then(response => {
        //Se agrega / actualiza cuenta recién autenticada
      }).catch(error => {
        console.error('Error:', error.message);
      });
    }
  }



  /**
   * Redirige al usuario hacia la aplicación de origen
   * o al mainApp en caso de no tener datos válidos.
   *   https://dev-app-local.com:4200/login?url_redirect=https://business.edutin.com
   *   https://dev-app-local.com:4200/signup?url_redirect=https://business.edutin.com
   */
  redirectToApp(): void {
    if (!this.tokenService.isTokenValid()) {
      this.safeRedirect(environment.mainApp);
      return;
    }

    const token = this.tokenService.getToken() ?? '';
    const refreshToken = this.tokenService.getRefreshToken() ?? '';
    const returnUrl = this.originDomainData?.returnUrl;
    const source = this.originDomainData?.source;

    // Caso especial para localhost
    if (source === 'localhost' && returnUrl && token) {
      let appToReturn = this.appendQueryParam(returnUrl, 'userToken', token);
      if (refreshToken) {
        appToReturn = this.appendQueryParam(appToReturn, 'refreshToken', refreshToken);
      }
      this.safeRedirect(appToReturn);
      return;
    }

    // Redirección genérica
    this.safeRedirect(returnUrl || environment.mainApp);
  }


  /**
   * Agrega un query param a una URL de forma segura.
   */
  private appendQueryParam(url: string, key: string, value: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
  }

  /**
   * Centraliza la forma de redirigir:
   * - Si es dominio externo => window.location.href
   * - Si es ruta interna => Angular Router
   */
  private safeRedirect(url: string): void {
    window.location.href = url;
  }



  toggleContinueAsAccounts(field: 'continue_as' | 'accounts_list'): void {
    // Alternar la visibilidad entre listado o cuenta unica, segun el campo
    this.accountVisibility[field] = !this.accountVisibility[field];

    // Ocultar la otra vista si la actual se activa
    if (this.accountVisibility[field]) {
      const otherField = field === 'continue_as' ? 'accounts_list' : 'continue_as';
      this.accountVisibility[otherField] = false;
    }
  }


  textOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null; // No validar si está vacío (usar Validators.required por separado)
      }

      // Expresión regular que solo permite letras (mayúsculas, minúsculas) y espacios
      const textOnlyRegex = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/;

      const isValid = textOnlyRegex.test(control.value);

      return isValid ? null : { textOnly: { value: control.value } };
    };
  }


  /**
    * Validador personalizado que acepta un email o un número de celular válido
    * Se usa para validar que la información ingresada en el input, sea un email o celular válido
    */
  emailOrPhoneValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;

    if (!value) return null;

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phonePattern = /^[0-9]{7,15}$/;

    if (emailPattern.test(value) || phonePattern.test(value)) {
      return null; // Es válido
    }

    return { invalidEmailOrPhone: true };
  }

  /**
   * Función para detectar si la entrada es email o cellphone
   */
  getIdentifierType(value: string): 'email' | 'phone' | null {
    if (!value) return null;

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const phonePattern = /^\+?[1-9]\d{1,14}$/; // E.164 estándar /^[0-9]{10,15}$/;

    if (emailPattern.test(value)) {
      return 'email';
    }
    if (phonePattern.test(value)) {
      return 'phone';
    }
    return null;
  }

  /**
   * Retorna el identificador formateado como correo de teléfono si es un número.
   * @param identifier - El identificador que puede ser un teléfono o un email.
   * @returns El identificador formateado.
   */
  getPhoneFormatted(identifier: string, longPhone: boolean = false): string {
    const isPhone = this.getIdentifierType(identifier) === 'phone';

    if (isPhone) {
      // Obtenemos el código del país sin guiones ni signos
      const rawCode = this.userCountry().country?.code?.replace(/-/g, '') || '';
      const cleanIdentifier = identifier.replace(/\s+/g, '');

      // Verificamos si el identificador ya tiene el indicativo (con o sin '+')
      const alreadyHasCode = cleanIdentifier.startsWith(rawCode) || cleanIdentifier.startsWith(`+${rawCode}`);
      const finalNumber = alreadyHasCode ? cleanIdentifier.replace(/^\+/, '') : `${rawCode}${cleanIdentifier}`;

      // Si se solicita la versión larga con el dominio
      if (longPhone) {
        return `+${finalNumber}@phone.edutin.com`;
      }

      // Si no se solicita la versión larga
      return `${finalNumber}`;
    }

    // Si no es tipo teléfono, simplemente se devuelve el identificador (email en este caso)
    return identifier;
  }

}
